import {
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import type { Href } from 'expo-router';
import { router } from 'expo-router';
import { useState } from 'react';

import { Button, Text, VStack, theme } from '@squire/ui';

import { routes } from '@/constants/routes';
import { useBnplIntentMutation } from '@/hooks/mutations/booking/useBnplIntentMutation';
import { useCancelBnplIntentMutation } from '@/hooks/mutations/booking/useCancelBnplIntentMutation';
import { useBnplExperimentVariant } from '@/hooks/payments/useBnplExperimentVariant';
import { useCustomerQuery } from '@/hooks/queries/customer/useCustomerQuery';
import { useScreenTranslations } from '@/hooks/useScreenTranslations';
import { BNPL_METHODS } from '@/utils/bnpl';
import type { BnplMethodType } from '@/utils/bnpl';
import {
  clearBnplPaymentFromSession,
  getBnplPaymentFromSession,
  setBnplPaymentFromSession,
} from '@/utils/bnplSessionStorage.web';
import { Logger } from '@/utils/logging';
import { getPaymentReturnUrl } from '@/utils/navigationUtils';
import { PaymentMethodType } from '@/utils/paymentMethods';
import { isUnmountedElementError } from '@/utils/stripeErrors';
import { Tracker } from '@/utils/tracking';
import {
  LD_EVENTS,
  type LdPaymentAttemptedPayload,
} from '@/utils/tracking/ldEvents';

import type { BnplFormProps } from './types';

const BnplForm = ({
  cart,
  shop,
  customer,
  addPaymentAndCustomer,
  onError,
}: BnplFormProps) => {
  const stripe = useStripe();
  const elements = useElements();
  const { mutateAsync, isPending } = useBnplIntentMutation();
  const { mutateAsync: cancelBnplIntent } = useCancelBnplIntentMutation();
  // Covers the whole submit — including the cancel-leftover phase, which
  // `isPending` (the intent mutation) does not.
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Stripe rejects submit on a PaymentElement that has not loaded.
  const [isPaymentElementReady, setIsPaymentElementReady] = useState(false);
  const { t } = useScreenTranslations('BnplBottomSheet');
  const { data: loggedInCustomer } = useCustomerQuery();
  const { variant, isEnrolled } = useBnplExperimentVariant(cart, shop);

  const entryType = loggedInCustomer ? 'authenticated' : 'guest';

  const getBnplIntentAndSaveInSession = async (paymentMethod: {
    type: BnplMethodType;
    id: string;
  }): Promise<boolean> => {
    const returnUrl = getPaymentReturnUrl(
      routes.bnplReturn({ shopRoute: shop.route, saleOrderId: cart.id }),
    );

    const response = await mutateAsync({
      cartId: cart.id,
      methodType: paymentMethod.type,
      paymentMethodId: paymentMethod.id,
      returnUrl,
      shopId: shop.id,
    });

    if (!response.redirectUrl) {
      onError(t('failedToCreateIntent'));
      return false;
    }

    setBnplPaymentFromSession({
      methodType: paymentMethod.type,
      paymentMethodId: paymentMethod.id,
      shopId: shop.id,
      shopRoute: shop.route,
      cartId: cart.id,
      paymentIntentId: response.paymentIntentId,
      clientSecret: response.clientSecret,
      status: response.status,
      barberId: cart.new_appointments?.at(0)?.barberId,
    });

    Tracker.track('bnpl_redirect_started', {
      methodType: paymentMethod.type,
    });

    // Fired here rather than at submit because this is the first point where
    // Stripe has resolved the concrete provider — everything earlier can only
    // report the generic 'bnpl' row. Attempts that fail before this (element
    // submit, createPaymentMethod) are knowingly not counted.
    if (isEnrolled) {
      const paymentAttemptedPayload: LdPaymentAttemptedPayload = {
        method: 'bnpl',
        bnpl_provider: paymentMethod.type,
        variant,
        shop_id: shop.id,
        entry_type: entryType,
      };

      Tracker.track(LD_EVENTS.paymentAttempted, paymentAttemptedPayload, {
        whitelist: ['launchDarkly'],
      });

      // The redirect below is a full-page navigation to the provider's own
      // domain, which unloads the SPA — flush explicitly so delivery doesn't
      // depend on winning that race.
      await Tracker.flush(['launchDarkly']);
    }

    router.push(response.redirectUrl as Href);
    return true;
  };

  /**
   * A leftover intent from an abandoned attempt keeps the cart locked
   * server-side ("payment in flight"), so a new attempt must cancel it first
   * (SQR-15310 / SQR-15683). Returns true when it is safe to proceed.
   */
  const settleLeftoverIntent = async (): Promise<boolean> => {
    const pending = getBnplPaymentFromSession(cart.id);
    if (!pending?.paymentIntentId) return true;

    try {
      const { status } = await cancelBnplIntent({
        paymentIntentId: pending.paymentIntentId,
        cartId: cart.id,
      });

      if (status === 'canceled') {
        clearBnplPaymentFromSession();
        return true;
      }

      if (status === 'succeeded' || status === 'processing') {
        // The customer already paid (e.g. finished in another tab) — never
        // start a second payment; show them their reservation.
        clearBnplPaymentFromSession();
        router.replace(
          routes.viewReservation({
            saleOrderId: pending.cartId,
            bnpl: 'true',
          }),
        );
        return false;
      }

      Logger.warn('Leftover BNPL intent in unexpected status on retry', {
        cartId: cart.id,
        paymentIntentId: pending.paymentIntentId,
        status,
      });
      onError(t('failedToCreateIntent'));
      return false;
    } catch (error) {
      Logger.error('Failed to cancel leftover BNPL intent on retry', {
        error,
        cartId: cart.id,
        paymentIntentId: pending.paymentIntentId,
      });
      onError(t('failedToCreateIntent'));
      return false;
    }
  };

  const handleSubmit = async () => {
    // `isPending` only covers the intent mutation, so it leaves the
    // cancel-leftover phase unguarded — two quick presses could each cancel
    // the same intent and then start duplicate cart/payment work.
    if (isSubmitting) return;

    if (!customer) {
      onError(t('missingCustomer'));
      return;
    }
    if (!stripe || !elements) {
      onError(t('stripeNotReady'));
      return;
    }
    if (!isPaymentElementReady) return;

    setIsSubmitting(true);
    let isRedirecting = false;
    try {
      if (!(await settleLeftoverIntent())) return;
      isRedirecting = await submitPayment();
    } finally {
      // Assigning `window.location` does not unload the page: it stays
      // interactive until the provider responds. Releasing here would let a
      // second press treat the intent just created as a leftover and cancel
      // it (SQR-16088), so on success the button stays locked until the
      // navigation commits.
      if (!isRedirecting) setIsSubmitting(false);
    }
  };

  const submitPayment = async (): Promise<boolean> => {
    if (!customer || !stripe || !elements) return false;

    try {
      const { error: submitError } = await elements.submit();
      if (submitError) {
        onError(submitError.message ?? t('failedToCreateIntent'));
        return false;
      }

      const { error: pmError, paymentMethod } =
        await stripe.createPaymentMethod({ elements });
      if (pmError || !paymentMethod) {
        onError(pmError?.message ?? t('failedToCreateIntent'));
        return false;
      }

      const rawType = paymentMethod.type;
      const methodType = (BNPL_METHODS as string[]).includes(rawType)
        ? (rawType as BnplMethodType)
        : null;
      if (!methodType) {
        onError(t('failedToCreateIntent'));
        return false;
      }

      // The cart upsert is fire-and-forget, so await its callbacks: the
      // submit is only finished once the intent has been created (or the
      // attempt has failed), otherwise the button unlatches mid-flight.
      return await new Promise<boolean>((resolve) => {
        addPaymentAndCustomer(
          {
            amount: cart.breakdown?.amountLeftForPayment ?? 0,
            type: PaymentMethodType.card,
            paymentToken: paymentMethod.id,
          },
          customer,
          {
            onSuccess: async () => {
              try {
                resolve(
                  await getBnplIntentAndSaveInSession({
                    type: methodType,
                    id: paymentMethod.id,
                  }),
                );
              } catch (error) {
                Logger.error('BNPL web payment failed', { error });
                onError(t('failedToCreateIntent'));
                resolve(false);
              }
            },
            onError: (error) => {
              Logger.error('BNPL web cart upsert failed', { error });
              onError(t('failedToCreateIntent'));
              resolve(false);
            },
          },
        );
      });
    } catch (error) {
      if (isUnmountedElementError(error)) {
        Logger.warn('Stripe payment field was not available', {
          message: (error as Error).message,
        });
        return false;
      }
      Logger.error('BNPL web payment failed', { error });
      onError(t('failedToCreateIntent'));
      return false;
    }
  };

  return (
    <VStack padding={theme.spacing[5]} space={theme.spacing[4]}>
      <PaymentElement
        onReady={() => setIsPaymentElementReady(true)}
        options={{
          defaultValues: {
            billingDetails: {
              phone: customer?.phone,
              email: customer?.email,
              name: `${customer?.firstName} ${customer?.lastName}`,
            },
          },
        }}
      />
      <Button
        testID="bnpl-submit-button"
        onPress={handleSubmit}
        disabled={!isPaymentElementReady}
        loading={isPending || isSubmitting}
      >
        <Text color="textPrimaryInverse" variant="lStrong">
          {t('next')}
        </Text>
      </Button>
    </VStack>
  );
};

BnplForm.displayName = 'BnplForm';
export { BnplForm };
